import type { EventEffect, EventScopeType, Severity, WorldData } from "../domain/types";


export type GmEffectType =
  | "demand_multiplier"
  | "fuel_price_delta"
  | "airspace_status"
  | "airport_capacity_multiplier"
  | "license_pressure"
  | "reputation_delta"
  | "maintenance_disruption";

export interface GmDraft {
  title: string;
  severity: Severity;
  scopeType: EventScopeType;
  scopeValue: string;
  reason: string;
  effectType: GmEffectType;
  effectValue: string;
  activateNow: boolean;
  durationHours: number;
}

export interface GmEventInput {
  title: string;
  severity: Severity;
  scopeType: EventScopeType;
  scopeValue: string;
  reason: string;
  effects: EventEffect[];
  activateNow: boolean;
  durationHours: number;
}

export const initialGmDraft: GmDraft = {
  title: "华东突发客流扰动",
  severity: "warning",
  scopeType: "region",
  scopeValue: "cn-east",
  reason: "GM 手工下发",
  effectType: "demand_multiplier",
  effectValue: "1.30",
  activateNow: true,
  durationHours: 6
};

export function scopeOptions(world: WorldData, scopeType: EventScopeType): string[] {
  if (scopeType === "global") return ["global"];
  if (scopeType === "region") return world.regions.map((region) => region.id);
  if (scopeType === "airport") return world.airports.map((airport) => airport.code);
  if (scopeType === "airspace") return world.airspaces.map((airspace) => airspace.id);
  return world.fuelZones.map((zone) => zone.id);
}

export function defaultEffectValue(effectType: GmEffectType): string {
  if (effectType === "demand_multiplier") return "1.25";
  if (effectType === "fuel_price_delta") return "150";
  if (effectType === "airspace_status") return "closed";
  if (effectType === "airport_capacity_multiplier") return "0.50";
  if (effectType === "license_pressure") return "-8";
  if (effectType === "reputation_delta") return "-2";
  return "0.12";
}

export function draftToEffect(draft: GmDraft): EventEffect {
  const value = Number(draft.effectValue);
  if (draft.effectType === "demand_multiplier") return { type: "demand_multiplier", multiplier: finiteEffectNumber(value, 1.25) };
  if (draft.effectType === "fuel_price_delta") return { type: "fuel_price_delta", usdPerTonDelta: finiteEffectNumber(value, 150) };
  if (draft.effectType === "airspace_status") {
    let status: "open" | "restricted" | "closed" = "closed";
    if (draft.effectValue === "open" || draft.effectValue === "restricted" || draft.effectValue === "closed") {
      status = draft.effectValue;
    }
    return { type: "airspace_status", status };
  }
  if (draft.effectType === "airport_capacity_multiplier") return { type: "airport_capacity_multiplier", multiplier: finiteEffectNumber(value, 0.5) };
  if (draft.effectType === "license_pressure") return { type: "license_pressure", progressDelta: finiteEffectNumber(value, -8) };
  if (draft.effectType === "reputation_delta") return { type: "reputation_delta", points: finiteEffectNumber(value, -2) };
  return { type: "maintenance_disruption", defectRate: finiteEffectNumber(value, 0.12), downtimeDays: 1 };
}

function finiteEffectNumber(value: number, fallback: number): number {
  if (Number.isFinite(value)) {
    return value;
  }
  return fallback;
}
